import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { posts, localizePost } from "./posts/posts";
import { useLang } from "../i18n/LangContext";
import { motion } from "framer-motion";
import { FiCalendar } from "react-icons/fi";
import { Tag } from "lucide-react";

const POSTS_PER_PAGE = 6;

export default function BlogList() {
  const { lang, t, localePath, formatDate } = useLang();
  const [currentPage, setCurrentPage] = useState(1);
  const prevPage = useRef(currentPage);

  const sortedPosts = posts.map((p) => localizePost(p, lang)).sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  );
  const totalPages = Math.ceil(sortedPosts.length / POSTS_PER_PAGE);
  const start = (currentPage - 1) * POSTS_PER_PAGE;
  const currentPosts = sortedPosts.slice(start, start + POSTS_PER_PAGE);

  useEffect(() => {
    if (currentPage > prevPage.current) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (currentPage < prevPage.current) {
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    }
    prevPage.current = currentPage;
  }, [currentPage]);

  // Scroll arriba al cambiar de categoría
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setCurrentPage(1);
  }, []);

  return (
    <div id="blog" className="max-w-4xl mx-auto py-6 sm:py-12">
      {/* Cabecera del blog */}
      <div className="text-center">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4">
          {t("blog.title")}
        </h1>
        <blockquote className="italic text-slate-500 text-lg dark:text-slate-400 max-w-2xl mx-auto">
          {t("blog.quote")}
          <footer className="mt-1 text-base not-italic">
            — Robert A. Heinlein
          </footer>
        </blockquote>
      </div>
      <hr className="my-5 sm:my-7 border-slate-300 dark:border-slate-700"/>

      {/* Lista de posts */}
      <div className="space-y-6">
        {currentPosts.map((post, index) => {
          const isComingSoon = !post.slug;

          return (
            <motion.article
              key={post.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.08, duration: 0.4 }}
              className={`
                p-4 md:p-6 rounded-2xl shadow-md flex items-center justify-between relative
                ${isComingSoon 
                  ? "bg-slate-200 dark:bg-slate-800 cursor-not-allowed opacity-60"
                  : "bg-white dark:bg-slate-900 hover:shadow-xl transition-shadow border border-transparent"
                }
              `}
            >
              <div className="flex-1">
                {/* Título */}
                <h3 className={`text-lg font-semibold mb-2 ${isComingSoon ? "text-slate-500 dark:text-slate-400" : "text-slate-900 dark:text-slate-100"}`}>
                  {post.title}
                </h3>

                {/* Fecha con icono */}
                <div className="flex items-center text-sm text-slate-500 dark:text-slate-400 mb-2">
                  <FiCalendar className="mr-1"/>
                  {isComingSoon
                    ? t("blog.comingSoon")
                    : formatDate(post.date, {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })
                  }
                  {!isComingSoon && !post.translated && (
                    <span className="ml-3 italic">· {t("blog.onlySpanish")}</span>
                  )}
                </div>

                {/* Extracto */}
                <p className={`text-base ${isComingSoon ? "text-slate-500 dark:text-slate-400" : "text-slate-600 dark:text-slate-300"}`}>
                  {post.excerpt}
                </p>

                {/* Etiqueta de categoría */}
                {post.category && (
                  <div className={`inline-flex items-center px-3 py-1 mt-3 text-xs font-semibold rounded-full
                    ${isComingSoon ? "bg-slate-300 text-slate-500 dark:bg-slate-700 dark:text-slate-400" : "bg-indigo-100 text-indigo-800"}`}>
                    <Tag className="mr-2 w-4 h-4" />
                    <span>{post.category}</span>
                  </div>
                )}
              </div>

              {/* Solo si hay slug hacemos clic */}
              {!isComingSoon && (
                <Link to={localePath(`/blog/${post.slug}`)} className="absolute inset-0" />
              )}
            </motion.article>
          );
        })}
      </div>

      {/* Paginación */}
      <div className="mt-8 flex justify-center gap-2 flex-wrap">
        {Array.from({ length: totalPages }, (_, i) => (
          <button
            key={i + 1}
            onClick={() => setCurrentPage(i + 1)}
            className={`px-3 py-1 rounded-md ${
              currentPage === i + 1
                ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                : "bg-slate-200 text-slate-900 dark:bg-slate-800 dark:text-slate-100"
            }`}
          >
            {i + 1}
          </button>
        ))}
      </div>
    </div>
  );
}
